/**
 * Demo mode: serves the dashboard from an in-memory dataset instead of the API.
 * Installed once at app boot; inert until a demo login.
 *
 * Fail closed — while active, unmatched routes are refused rather than
 * forwarded, so an overlooked endpoint cannot write to production.
 */

import axios, {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { API_ROOT } from "../api";
import { matchRoute } from "./handlers";
import { toLoginResponse } from "./serializers";
import {
  exitDemoMode,
  getDemoIdentity,
  getDemoRole,
  isDemoCredentials,
  isDemoMode,
  mintDemoToken,
  setDemoRole,
  type DemoRole,
} from "./session";
import { findUser } from "./store";

export {
  DEMO_EMAIL,
  DEMO_PASSWORD,
  exitDemoMode,
  getDemoRole,
  isDemoMode,
  mintDemoToken,
  setDemoRole,
} from "./session";
export { resetDemoData } from "./store";
export type { DemoRole } from "./session";

/**
 * Public read-only endpoints that keep hitting the live API, so the marketing
 * pages and publications still render. The CMS proxy is mounted at `/content`,
 * not `/contentful`. Writes are absent here on purpose.
 */
const PASSTHROUGH: { method: string; matches: (path: string) => boolean }[] = [
  { method: "GET", matches: (p) => p.startsWith("/content/") },
  { method: "GET", matches: (p) => p === "/contributions" },
  { method: "GET", matches: (p) => p.startsWith("/contributions/slug/") },
  { method: "GET", matches: (p) => p.startsWith("/contributions/download/") },
  { method: "GET", matches: (p) => p.startsWith("/contributions/cover/") },
];

function isPassthrough(method: string, path: string): boolean {
  return PASSTHROUGH.some(
    (rule) => rule.method === method && rule.matches(path),
  );
}

/** Posting to these means the visitor wants a real account, so demo mode exits. */
const REAL_ACCOUNT_PATHS = new Set([
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/resend-verification-email",
]);

function resolveUrl(config: InternalAxiosRequestConfig): string {
  const url = config.url ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${config.baseURL ?? ""}${url}`;
}

function parseBody(data: unknown): Record<string, unknown> {
  if (!data) return {};
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  if (typeof data === "object") return data as Record<string, unknown>;
  return {};
}

function makeResponse(
  config: InternalAxiosRequestConfig,
  status: number,
  data: unknown,
): AxiosResponse {
  return {
    data,
    status,
    statusText: status === 201 ? "Created" : "OK",
    headers: {},
    config,
    request: {},
  };
}

function makeError(
  config: InternalAxiosRequestConfig,
  status: number,
  data: unknown,
): AxiosError {
  const response = makeResponse(config, status, data);
  response.statusText = "Error";
  const message =
    (data as { message?: string })?.message ?? `Request failed with status ${status}`;
  return new AxiosError(
    message,
    status === 404 ? AxiosError.ERR_BAD_REQUEST : AxiosError.ERR_BAD_RESPONSE,
    config,
    {},
    response,
  );
}

/** Short-circuits the request; axios never opens a connection. */
function respondWith(
  config: InternalAxiosRequestConfig,
  status: number,
  data: unknown,
): InternalAxiosRequestConfig {
  config.adapter = async () => {
    if (status >= 400) throw makeError(config, status, data);
    return makeResponse(config, status, data);
  };
  return config;
}

function refuse(
  config: InternalAxiosRequestConfig,
  method: string,
  path: string,
): InternalAxiosRequestConfig {
  const message = `Demo mode: ${method} ${path} is not available. No request was sent to the live API.`;
  // 501 so it reads as a demo gap, not a real server error.
  return respondWith(config, 501, { message, demoBlocked: true });
}

function handleDemoLogin(
  config: InternalAxiosRequestConfig,
  body: Record<string, unknown>,
): InternalAxiosRequestConfig {
  const requestedRole = (body.demoRole as DemoRole | undefined) ?? "admin";
  setDemoRole(requestedRole);

  const identity = getDemoIdentity(requestedRole);
  const user = findUser(identity.userId);
  if (!user) {
    return respondWith(config, 500, { message: "Demo user missing from fixtures" });
  }
  return respondWith(config, 200, toLoginResponse(user, mintDemoToken(requestedRole)));
}

let installed = false;

export function installDemoMode(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const url = resolveUrl(config);
    // Anything off our own API (Contentful CDN, Nominatim geocoding) is left alone.
    if (!url.startsWith(API_ROOT)) return config;

    const [rawPath] = url.slice(API_ROOT.length).split("#");
    const [path, queryString = ""] = rawPath.split("?");
    const method = (config.method ?? "get").toUpperCase();
    const body = parseBody(config.data);

    // Watched even outside demo mode — this is how a demo session starts.
    if (method === "POST" && path === "/auth/login") {
      if (isDemoCredentials(body)) return handleDemoLogin(config, body);
      // Real credentials: leave demo mode rather than stranding them. Guarded,
      // because exitDemoMode() clears localStorage and must not run otherwise.
      if (isDemoMode()) exitDemoMode();
      return config;
    }

    if (method === "POST" && REAL_ACCOUNT_PATHS.has(path)) {
      if (isDemoMode()) exitDemoMode();
      return config;
    }

    const role = getDemoRole();
    if (!role) return config;

    if (isPassthrough(method, path)) return config;

    const matched = matchRoute(method, path);
    if (!matched) return refuse(config, method, path);

    const result = matched.handler({
      params: matched.params,
      body,
      query: new URLSearchParams(queryString),
      role,
    });
    return respondWith(config, result.status, result.data);
  });

  installFetchGuard();
}

/**
 * `WrittenContributionsForm` uploads with raw `fetch`, bypassing the axios
 * interceptor. Blocked here so fail-closed holds for every request.
 */
function installFetchGuard(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isDemoMode()) return originalFetch(input, init);

    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    if (url.startsWith(API_ROOT)) {
      const path = url.slice(API_ROOT.length).split("?")[0];
      const method = (init?.method ?? "GET").toUpperCase();
      if (!isPassthrough(method, path)) {
        return new Response(
          JSON.stringify({
            message: "Demo mode: this action is not available.",
            demoBlocked: true,
          }),
          { status: 501, headers: { "Content-Type": "application/json" } },
        );
      }
    }
    return originalFetch(input, init);
  };
}
